import { DragEvent, PointerEvent, memo, useCallback } from 'react'

import { Item } from '@inno-sdks/files'
import {
  CrossAppDndAction,
  CrossAppDndItemKind,
  createCrossAppDragPayload,
  writeCrossAppDndPayload
} from '@inno-tools/container-bridge'
import { ContextMenu, ContextMenuTriggerOnActions } from '@inno-ui/context-menu'
import cn from 'classnames'
import {
  useDesktopStore,
  useSelectionStore
} from 'modules/desktop/pages/MainPage/stores'
import { useShortcutGroupStore } from 'modules/desktop/pages/MainPage/stores/shortcutGroupStore'
import { isShortcutMemberLocked } from 'modules/desktop/shortcutGroups'
import { useTranslation } from 'react-i18next'
import { ShortcutGridItem } from 'shared/types/desktop-grid'

import {
  BaseItem,
  BaseItemProps
} from '../../../../pages/MainPage/components/DesktopGrid/components/BaseItem'
import { getSystemShortcutDisplayName } from '../../constants'
import { useInlineRename } from '../../hooks'
import styles from './GridViewItem.module.scss'
import { GridItemContent } from './components/GridItemContent'
import { useGridItemContextMenu, useGridItemSelection } from './hooks'

export interface GridViewItemProps extends Omit<BaseItemProps, 'item'> {
  item: Item
  itemMetaData: ShortcutGridItem
  isFolderDropTarget?: boolean
}

export const GridViewItem = memo<GridViewItemProps>(
  ({
    onDragStart,
    onPointerMove,
    item,
    itemMetaData,
    isFolderDropTarget = false,
    ...props
  }) => {
    const { t } = useTranslation(undefined, {
      keyPrefix: 'desktop.shortcut_item'
    })
    const displayName = getSystemShortcutDisplayName(item, item.name, t)
    const subLabel =
      itemMetaData.subLabel ?? (itemMetaData.isPinned ? t('pinned') : undefined)
    const rename = useInlineRename(item)
    const isNewlyCreated = useDesktopStore(
      state => state.highlightedShortcutId === item.id
    )
    const shortcutGroups = useShortcutGroupStore(state => state.groups)
    const isLockedShortcut = isShortcutMemberLocked(
      shortcutGroups,
      itemMetaData.id
    )

    const {
      isSelected,
      selectionCount,
      isGroupContext,
      isLoading,
      showStillConnectingMessage,
      handleOnClick,
      handleOnDoubleClick,
      handleOnKeyDown
    } = useGridItemSelection({
      item,
      itemMetaData,
      isRenaming: rename.isRenaming
    })

    const {
      isContextMenuOpen,
      setIsContextMenuOpen,
      closeContextMenu,
      touchHandlers,
      options
    } = useGridItemContextMenu({
      item,
      itemMetaData,
      isGroupContext,
      selectionCount,
      onRename: rename.start
    })

    const handleDragStart = useCallback(
      (itemId: string) => (e: DragEvent<HTMLDivElement>) => {
        if (itemMetaData.isPinned || isLockedShortcut) {
          e.preventDefault()
          return
        }
        // Drag the whole selection if this item is in it, else just this one.
        // widgets have no fileId, so only shortcuts go into the payload.
        const selectedIds = useSelectionStore.getState().selectedIds
        const fileIds = selectedIds.has(itemMetaData.id)
          ? useDesktopStore
              .getState()
              .items.filter(i => selectedIds.has(i.id))
              .filter(i => i.itemType === 'shortcut')
              .filter(i => !i.isPinned)
              .filter(i => !isShortcutMemberLocked(shortcutGroups, i.id))
              .map(i => i.fileId)
          : [item.id]

        // Bridge payload + marker MIME drives cross-app DnD detection/overlay.
        writeCrossAppDndPayload(
          e.dataTransfer,
          createCrossAppDragPayload({
            sourceApp: 'container-app',
            action: CrossAppDndAction.MOVE,
            itemKind: CrossAppDndItemKind.CLUSTER_FILES_ITEM,
            itemIds: fileIds
          })
        )

        closeContextMenu()
        onDragStart(itemId)(e)
      },
      [
        item.id,
        itemMetaData.id,
        itemMetaData.isPinned,
        isLockedShortcut,
        onDragStart,
        closeContextMenu,
        shortcutGroups
      ]
    )

    const handlePointerMove = useCallback(
      (e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'touch' && isContextMenuOpen) {
          closeContextMenu()
        }
        onPointerMove?.(e)
      },
      [onPointerMove, isContextMenuOpen, closeContextMenu]
    )

    return (
      <BaseItem
        {...props}
        className={cn(props.className, {
          [styles.newlyCreated]: isNewlyCreated
        })}
        itemMetaData={itemMetaData}
        innerClassName={cn(styles.container, {
          [styles.selected]: isSelected,
          [styles.folderDropTarget]: isFolderDropTarget
        })}
        onClick={rename.isRenaming ? undefined : handleOnClick}
        onDoubleClick={rename.isRenaming ? undefined : handleOnDoubleClick}
        onKeyDown={rename.isRenaming ? undefined : handleOnKeyDown}
        ariaLabel={[displayName, subLabel].filter(Boolean).join(', ')}
        ariaBusy={isLoading}
        disabled={props.disabled || isLoading}
        onDragStart={handleDragStart}
        onPointerMove={handlePointerMove}
        disableNativeDrag={
          props.disableNativeDrag ||
          rename.isRenaming ||
          itemMetaData.isPinned ||
          isLockedShortcut
        }
        disableContextMenu
      >
        <ContextMenu
          disabled={rename.isRenaming}
          isOpen={isContextMenuOpen}
          setIsOpen={setIsContextMenuOpen}
          options={options}
          zIndex={10}
          stopEventPropagation
          triggerOnAction={ContextMenuTriggerOnActions.OnContextMenu}
          triggerNode={
            <GridItemContent
              item={item}
              displayName={displayName}
              row={itemMetaData.position.row}
              col={itemMetaData.position.col}
              visualStyle={itemMetaData.visualStyle}
              isPinned={itemMetaData.isPinned}
              subLabel={subLabel}
              isLoading={isLoading}
              showStillConnectingMessage={showStillConnectingMessage}
              isRenaming={rename.isRenaming}
              onRenameSubmit={rename.onSubmit}
              onRenameCancel={rename.onCancel}
              {...touchHandlers}
            />
          }
        />
      </BaseItem>
    )
  }
)
